import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import {
  ArrowLeft,
  BookOpen,
  ChevronRight,
  Folder,
  Globe,
  LayoutGrid,
  Library,
  Loader2,
  Search,
  Upload,
  X,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { GoogleDriveIcon } from "@/components/icons/GoogleDriveIcon";
import { cn } from "@/lib/utils";
import {
  downloadGoogleDriveFile,
  getGoogleDriveStatus,
  googleDriveDownloadToFile,
  isGoogleDriveReconnectRequiredError,
  startGoogleDriveConnect,
} from "@/lib/api/google-drive";
import {
  CONTRACT_PICKER_MIME_TYPES,
  canUseIframePicker,
  getGoogleDriveReturnPath,
  openGoogleDriveOnePick,
  openGooglePicker,
} from "@/lib/google-picker";
import { useDriveOnePickReturn } from "@/hooks/useDriveOnePickReturn";
import { useGoogleDriveConnectReturn } from "@/hooks/useGoogleDriveConnectReturn";
import {
  EMPTY_KNOWLEDGE_SCOPE_SELECTION,
  countSelectedLibraries,
  isPublicLibrariesFullySelected,
  isWorkspaceFullySelected,
  toggleAllPublicLibraries,
  togglePrivateLibrary,
  togglePublicLibrary,
  toggleWorkspace,
  type KnowledgeScopeOptions,
  type KnowledgeScopeSelection,
  type KnowledgeScopeWorkspaceOption,
} from "../lib/knowledge-scope";
import { fetchKnowledgeScopeOptions } from "../lib/knowledge-scope-api";

/** `root` opens General tab; `workspaces` opens Workspaces tab. */
export type ComposerPlusMenuView = "root" | "workspaces" | "libraries" | "public";

type TabId = "general" | "workspaces";
type WorkspacePane = "list" | "libraries" | "public";

type ComposerPlusMenuProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** View to show when the menu opens (e.g. Manage scope → workspaces). */
  initialView?: ComposerPlusMenuView;
  disabled?: boolean;
  /** When a slash command is selected: block Web search and Workspaces. */
  searchModesLocked?: boolean;
  webSearchEnabled: boolean;
  knowledgeSearchEnabled: boolean;
  knowledgeScopeSelection: KnowledgeScopeSelection | null;
  onAttachDocs: () => void;
  onAttachFile?: (file: File) => void;
  onWebSearchChange?: (enabled: boolean) => void;
  onKnowledgeSearchChange?: (enabled: boolean) => void;
  onKnowledgeScopeApply?: (selection: KnowledgeScopeSelection) => void;
};

function matchesSearch(name: string, query: string) {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return true;
  return name.toLowerCase().includes(normalized);
}

function MenuSwitch({
  checked,
  disabled,
  onCheckedChange,
  label,
}: {
  checked: boolean;
  disabled?: boolean;
  onCheckedChange: (next: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={(event) => {
        event.stopPropagation();
        onCheckedChange(!checked);
      }}
      className={cn(
        "relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors",
        checked ? "bg-violet-500" : "bg-[#cbd5e1] dark:bg-slate-600",
        disabled && "cursor-not-allowed opacity-50",
      )}
    >
      <span
        className={cn(
          "pointer-events-none absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform",
          checked ? "left-4" : "left-0.5",
        )}
      />
    </button>
  );
}

export function ComposerPlusMenu({
  open,
  onOpenChange,
  initialView = "root",
  disabled = false,
  searchModesLocked = false,
  webSearchEnabled,
  knowledgeSearchEnabled: _knowledgeSearchEnabled,
  knowledgeScopeSelection,
  onAttachDocs,
  onAttachFile,
  onWebSearchChange,
  onKnowledgeSearchChange,
  onKnowledgeScopeApply,
}: ComposerPlusMenuProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [tab, setTab] = useState<TabId>("general");
  const [workspacePane, setWorkspacePane] = useState<WorkspacePane>("list");
  const [activeWorkspaceId, setActiveWorkspaceId] = useState<string | null>(null);
  const [options, setOptions] = useState<KnowledgeScopeOptions | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  const [draftWebSearch, setDraftWebSearch] = useState(false);
  const [draftScope, setDraftScope] = useState<KnowledgeScopeSelection>(
    EMPTY_KNOWLEDGE_SCOPE_SELECTION,
  );

  const [driveAvailable, setDriveAvailable] = useState(false);
  const [driveReconnectRequired, setDriveReconnectRequired] = useState(false);
  const [driveBusy, setDriveBusy] = useState(false);
  const [driveImporting, setDriveImporting] = useState(false);
  const [driveError, setDriveError] = useState<string | null>(null);
  const [externalPickerActive, setExternalPickerActive] = useState(false);

  const refreshDriveStatus = useCallback(async () => {
    try {
      const status = await getGoogleDriveStatus();
      setDriveAvailable(status.connected && !status.reconnectRequired);
      setDriveReconnectRequired(status.reconnectRequired);
    } catch {
      setDriveAvailable(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    const preferWorkspaces = initialView !== "root" && !searchModesLocked;
    setTab(preferWorkspaces ? "workspaces" : "general");
    setWorkspacePane("list");
    setActiveWorkspaceId(null);
    setSearchQuery("");
    setDraftWebSearch(webSearchEnabled);
    setDraftScope(knowledgeScopeSelection ?? EMPTY_KNOWLEDGE_SCOPE_SELECTION);
    setDriveError(null);
    void refreshDriveStatus();
    // Only re-init when opening / changing initialView — not when parent props
    // update after auto-apply (that would reset tab/pane/search mid-session).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional
  }, [open, initialView, refreshDriveStatus]);

  useEffect(() => {
    if (!searchModesLocked) return;
    setTab("general");
    setWorkspacePane("list");
    setActiveWorkspaceId(null);
    setDraftWebSearch(false);
  }, [searchModesLocked]);

  useEffect(() => {
    if (!open || tab !== "workspaces") return;

    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    void fetchKnowledgeScopeOptions()
      .then((next) => {
        if (!cancelled) setOptions(next);
      })
      .catch((err) => {
        if (!cancelled) {
          setLoadError(err instanceof Error ? err.message : "Failed to load workspaces");
          setOptions(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, tab]);

  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: MouseEvent) {
      if (driveBusy || externalPickerActive) return;
      const target = event.target as Node | null;
      if (!target) return;
      if (panelRef.current?.contains(target)) return;
      if (target instanceof Element && target.closest("[data-composer-plus-trigger]")) return;
      onOpenChange(false);
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      if (driveBusy || externalPickerActive) return;
      event.preventDefault();
      onOpenChange(false);
    }

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onOpenChange, driveBusy, externalPickerActive]);

  const attachDownloadedFile = useCallback(
    (file: File) => {
      onAttachFile?.(file);
      onOpenChange(false);
    },
    [onAttachFile, onOpenChange],
  );

  const importFirstDriveFile = useCallback(
    async (selected: { id: string; resourceKey?: string | null; accessToken?: string }[]) => {
      if (selected.length === 0) return;
      const first = selected[0]!;
      setDriveBusy(true);
      setDriveImporting(true);
      setDriveError(null);
      try {
        const downloaded = await downloadGoogleDriveFile({
          fileId: first.id,
          resourceKey: first.resourceKey,
          accessToken: first.accessToken,
        });
        attachDownloadedFile(googleDriveDownloadToFile(downloaded));
      } catch (err) {
        if (isGoogleDriveReconnectRequiredError(err)) {
          setDriveAvailable(false);
          setDriveReconnectRequired(true);
          setDriveError("Google Drive authorization has expired. Reconnect to continue.");
        } else {
          setDriveError(err instanceof Error ? err.message : "Failed to import from Google Drive");
        }
      } finally {
        setDriveBusy(false);
        setDriveImporting(false);
      }
    },
    [attachDownloadedFile],
  );

  useDriveOnePickReturn({
    // Keep enabled while composer is mounted so return-from-Drive works if the dialog closed.
    onImport: async ({ files }) => {
      const file = files[0];
      if (!file) return;
      attachDownloadedFile(file);
    },
    onError: (message) => {
      if (message.includes("expired")) {
        setDriveAvailable(false);
        setDriveReconnectRequired(true);
      }
      setDriveError(message);
    },
    onBusyChange: setDriveImporting,
  });

  useGoogleDriveConnectReturn({
    onConnected: () => {
      void refreshDriveStatus();
    },
    onError: (message) => setDriveError(message),
  });

  const activeWorkspace: KnowledgeScopeWorkspaceOption | null = useMemo(() => {
    if (!options || !activeWorkspaceId) return null;
    return options.workspaces.find((workspace) => workspace.id === activeWorkspaceId) ?? null;
  }, [options, activeWorkspaceId]);

  const filteredWorkspaces = useMemo(() => {
    if (!options) return [];
    return options.workspaces.filter(
      (workspace) =>
        matchesSearch(workspace.name, searchQuery) ||
        workspace.libraries.some((library) => matchesSearch(library.name, searchQuery)),
    );
  }, [options, searchQuery]);

  const filteredLibraries = useMemo(() => {
    if (!activeWorkspace) return [];
    return activeWorkspace.libraries.filter((library) => matchesSearch(library.name, searchQuery));
  }, [activeWorkspace, searchQuery]);

  const filteredPublic = useMemo(() => {
    if (!options) return [];
    return options.publicLibraries.filter((library) => matchesSearch(library.name, searchQuery));
  }, [options, searchQuery]);

  function handleClose() {
    if (driveBusy || externalPickerActive) return;
    onOpenChange(false);
  }

  function commitSelection(nextWebSearch: boolean, nextScope: KnowledgeScopeSelection) {
    if (searchModesLocked && (nextWebSearch || countSelectedLibraries(nextScope) > 0)) {
      return;
    }
    setDraftWebSearch(nextWebSearch);
    setDraftScope(nextScope);
    const scopeCount = countSelectedLibraries(nextScope);
    if (nextWebSearch) {
      onWebSearchChange?.(true);
      onKnowledgeSearchChange?.(false);
      onKnowledgeScopeApply?.(nextScope);
    } else if (scopeCount > 0) {
      onWebSearchChange?.(false);
      onKnowledgeScopeApply?.(nextScope);
      onKnowledgeSearchChange?.(true);
    } else {
      onWebSearchChange?.(false);
      onKnowledgeSearchChange?.(false);
      onKnowledgeScopeApply?.(EMPTY_KNOWLEDGE_SCOPE_SELECTION);
    }
  }

  async function handleConnectDrive() {
    setDriveError(null);
    try {
      const { url } = await startGoogleDriveConnect(getGoogleDriveReturnPath());
      window.location.href = url;
    } catch (err) {
      setDriveError(err instanceof Error ? err.message : "Failed to start Google Drive connection");
    }
  }

  async function handleGoogleDriveClick() {
    if (disabled || driveBusy) return;
    setDriveError(null);

    if (!driveAvailable || driveReconnectRequired) {
      await handleConnectDrive();
      return;
    }

    setDriveBusy(true);
    try {
      const useIframe = await canUseIframePicker();
      if (useIframe) {
        flushSync(() => setExternalPickerActive(true));
        try {
          const selected = await openGooglePicker({
            multiple: false,
            mimeTypes: CONTRACT_PICKER_MIME_TYPES,
          });
          if (selected.length > 0) {
            await importFirstDriveFile(selected);
          }
        } finally {
          window.requestAnimationFrame(() => setExternalPickerActive(false));
        }
        return;
      }

      await openGoogleDriveOnePick({
        multiple: false,
        mimeTypes: CONTRACT_PICKER_MIME_TYPES,
        returnPath: getGoogleDriveReturnPath(),
      });
    } catch (err) {
      if (isGoogleDriveReconnectRequiredError(err)) {
        setDriveAvailable(false);
        setDriveReconnectRequired(true);
        setDriveError("Google Drive authorization has expired. Reconnect to continue.");
      } else {
        setDriveError(err instanceof Error ? err.message : "Failed to open Google Drive");
      }
    } finally {
      setDriveBusy(false);
    }
  }

  return (
    <>
      {driveImporting ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          role="status"
          aria-live="polite"
          aria-label="Importing from Google Drive"
        >
          <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-[#111b35] px-5 py-4 text-white shadow-xl">
            <Loader2 className="h-5 w-5 shrink-0 animate-spin text-violet-400" />
            <span className="text-sm font-medium">Importing from Google Drive…</span>
          </div>
        </div>
      ) : null}
      {open ? (
    <div
      ref={panelRef}
      role="dialog"
      aria-label="Add to chat"
      className={cn(
        "absolute bottom-full left-0 z-30 mb-2 flex w-[22rem] max-w-[calc(100vw-2rem)] max-h-[min(28rem,70vh)] flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#111b35] text-white shadow-xl",
        externalPickerActive && "pointer-events-none opacity-0",
      )}
    >
      <div className="shrink-0 border-b border-white/10 px-4 pb-0 pt-3.5">
        <div className="mb-3 flex items-start justify-between gap-2">
          <h2 className="text-sm font-semibold text-white">Add to chat</h2>
          <button
            type="button"
            aria-label="Close"
            disabled={driveBusy || externalPickerActive}
            onClick={handleClose}
            className="rounded-md p-0.5 text-slate-400 transition-colors hover:bg-white/5 hover:text-white disabled:opacity-50"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex gap-4" role="tablist" aria-label="Add to chat sections">
          <button
            type="button"
            role="tab"
            aria-selected={tab === "general"}
            onClick={() => {
              setTab("general");
              setWorkspacePane("list");
            }}
            className={cn(
              "relative flex items-center gap-1.5 pb-2.5 text-sm font-medium transition-colors",
              tab === "general" ? "text-white" : "text-slate-400 hover:text-slate-200",
            )}
          >
            <LayoutGrid className="h-3.5 w-3.5" />
            General
            {tab === "general" ? (
              <span className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-violet-500" />
            ) : null}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "workspaces"}
            disabled={searchModesLocked}
            title={
              searchModesLocked
                ? "Workspaces unavailable while a slash command is selected"
                : undefined
            }
            onClick={() => {
              if (searchModesLocked) return;
              setTab("workspaces");
              setWorkspacePane("list");
              setSearchQuery("");
              setActiveWorkspaceId(null);
            }}
            className={cn(
              "relative flex items-center gap-1.5 pb-2.5 text-sm font-medium transition-colors",
              tab === "workspaces" ? "text-white" : "text-slate-400 hover:text-slate-200",
              searchModesLocked && "cursor-not-allowed opacity-50",
            )}
          >
            <Folder className="h-3.5 w-3.5" />
            Workspaces
            {tab === "workspaces" ? (
              <span className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-violet-500" />
            ) : null}
          </button>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto px-4 py-3">
          {tab === "general" ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2.5">
                <button
                  type="button"
                  disabled={disabled || driveBusy}
                  onClick={() => {
                    onAttachDocs();
                    onOpenChange(false);
                  }}
                  className="flex flex-col items-start gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-3 text-left transition-colors hover:bg-white/10 disabled:opacity-50"
                >
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 text-slate-300">
                    <Upload className="h-4 w-4" />
                  </span>
                  <span>
                    <span className="block text-sm font-medium text-white">Upload files</span>
                    <span className="block text-xs text-slate-400">PDF, DOCX</span>
                  </span>
                </button>

                <button
                  type="button"
                  disabled={disabled || driveBusy}
                  onClick={() => void handleGoogleDriveClick()}
                  className="flex flex-col items-start gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-3 text-left transition-colors hover:bg-white/10 disabled:opacity-50"
                >
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 text-slate-300">
                    {driveBusy ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <GoogleDriveIcon className="h-4 w-4" />
                    )}
                  </span>
                  <span>
                    <span className="block text-sm font-medium text-white">
                      Google Drive
                    </span>
                    <span className="block text-xs text-slate-400">
                      {driveReconnectRequired
                        ? "Reconnect required"
                        : driveAvailable
                          ? "Cloud storage"
                          : "Connect Drive"}
                    </span>
                  </span>
                </button>
              </div>

              {driveError ? (
                <div className="rounded-lg border border-red-400/30 bg-red-500/10 px-3 py-2 text-xs text-red-300">
                  <p>{driveError}</p>
                  {driveReconnectRequired ? (
                    <button
                      type="button"
                      className="mt-1 font-medium underline"
                      onClick={() => void handleConnectDrive()}
                    >
                      Reconnect Google Drive
                    </button>
                  ) : null}
                </div>
              ) : null}

              <div className="flex w-full items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-3 py-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-white/10 text-slate-300">
                  <Globe className="h-4 w-4" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-white">Web search</span>
                </span>
                <MenuSwitch
                  checked={draftWebSearch}
                  disabled={disabled || searchModesLocked || !onWebSearchChange}
                  label="Web search"
                  onCheckedChange={(enabled) => {
                    if (searchModesLocked) return;
                    commitSelection(
                      enabled,
                      enabled ? EMPTY_KNOWLEDGE_SCOPE_SELECTION : draftScope,
                    );
                  }}
                />
              </div>
            </div>
          ) : null}

          {tab === "workspaces" && workspacePane === "list" ? (
            <div className="flex min-h-[12rem] flex-col">
              <div className="relative mb-2">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-500" />
                <Input
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Search workspaces"
                  className="h-8 border-white/10 bg-white/5 pl-8 text-sm text-white placeholder:text-slate-500"
                  aria-label="Search workspaces"
                />
              </div>
              <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto">
                {loading ? (
                  <div className="flex items-center justify-center gap-2 py-10 text-sm text-slate-400">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading…
                  </div>
                ) : loadError ? (
                  <div className="py-8 text-center text-sm text-red-400">{loadError}</div>
                ) : !options ||
                  (options.workspaces.length === 0 && options.publicLibraries.length === 0) ? (
                  <div className="py-8 text-center text-sm text-slate-400">
                    No workspaces available yet.
                  </div>
                ) : filteredWorkspaces.length === 0 &&
                  !(
                    options.publicLibraries.length > 0 &&
                    (matchesSearch("Public libraries", searchQuery) ||
                      options.publicLibraries.some((library) =>
                        matchesSearch(library.name, searchQuery),
                      ))
                  ) ? (
                  <div className="py-8 text-center text-sm text-slate-400">
                    No workspaces match “{searchQuery.trim()}”
                  </div>
                ) : (
                  <ul>
                    {filteredWorkspaces.map((workspace) => {
                      const fullyOn = isWorkspaceFullySelected(draftScope, workspace);
                      const itemLabel =
                        workspace.libraries.length === 1
                          ? "1 item"
                          : `${workspace.libraries.length} items`;
                      return (
                        <li
                          key={workspace.id}
                          className="flex min-w-0 w-full items-center gap-2 rounded-lg px-1 py-1 hover:bg-white/5"
                        >
                          <button
                            type="button"
                            className="flex min-w-0 flex-1 items-center gap-1 rounded-lg px-1 py-1 text-left"
                            onClick={() => {
                              setActiveWorkspaceId(workspace.id);
                              setSearchQuery("");
                              setWorkspacePane("libraries");
                            }}
                          >
                            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-white/10 text-slate-300">
                              <Folder className="h-4 w-4" />
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-sm font-medium text-white">
                                {workspace.name}
                              </span>
                              <span className="block text-xs text-slate-400">{itemLabel}</span>
                            </span>
                            <ChevronRight className="h-4 w-4 shrink-0 text-slate-500" />
                          </button>
                          <div className="flex shrink-0 items-center pr-1">
                            <MenuSwitch
                              checked={fullyOn}
                              disabled={disabled || workspace.libraries.length === 0}
                              label={`Toggle ${workspace.name}`}
                              onCheckedChange={() => {
                                commitSelection(false, toggleWorkspace(draftScope, workspace));
                              }}
                            />
                          </div>
                        </li>
                      );
                    })}
                    {options.publicLibraries.length > 0 &&
                    (matchesSearch("Public libraries", searchQuery) ||
                      options.publicLibraries.some((library) =>
                        matchesSearch(library.name, searchQuery),
                      )) ? (
                      <li className="flex min-w-0 w-full items-center gap-2 rounded-lg px-1 py-1 hover:bg-white/5">
                        <button
                          type="button"
                          className="flex min-w-0 flex-1 items-center gap-1 rounded-lg px-1 py-1 text-left"
                          onClick={() => {
                            setSearchQuery("");
                            setWorkspacePane("public");
                          }}
                        >
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-white/10 text-slate-300">
                            <Library className="h-4 w-4" />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-medium text-white">
                              Public libraries
                            </span>
                            <span className="block text-xs text-slate-400">
                              {options.publicLibraries.length === 1
                                ? "1 item"
                                : `${options.publicLibraries.length} items`}
                            </span>
                          </span>
                          <ChevronRight className="h-4 w-4 shrink-0 text-slate-500" />
                        </button>
                        <div className="flex shrink-0 items-center pr-1">
                          <MenuSwitch
                            checked={isPublicLibrariesFullySelected(
                              draftScope,
                              options.publicLibraries,
                            )}
                            disabled={disabled}
                            label="Toggle public libraries"
                            onCheckedChange={() => {
                              commitSelection(
                                false,
                                toggleAllPublicLibraries(draftScope, options.publicLibraries),
                              );
                            }}
                          />
                        </div>
                      </li>
                    ) : null}
                  </ul>
                )}
              </div>
            </div>
          ) : null}

          {tab === "workspaces" && workspacePane === "libraries" && activeWorkspace ? (
            <div className="flex min-h-[12rem] flex-col">
              <button
                type="button"
                onClick={() => {
                  setSearchQuery("");
                  setWorkspacePane("list");
                }}
                className="mb-1 flex items-center gap-1.5 text-sm font-medium text-white"
              >
                <ArrowLeft className="h-4 w-4" />
                <span className="truncate">{activeWorkspace.name}</span>
              </button>
              <div className="relative mb-2">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-500" />
                <Input
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Search libraries"
                  className="h-8 border-white/10 bg-white/5 pl-8 text-sm text-white placeholder:text-slate-500"
                  aria-label="Search libraries"
                />
              </div>
              <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto">
                {filteredLibraries.length === 0 ? (
                  <div className="py-8 text-center text-sm text-slate-400">
                    {activeWorkspace.libraries.length === 0
                      ? "No libraries in this workspace."
                      : `No libraries match “${searchQuery.trim()}”`}
                  </div>
                ) : (
                  <ul>
                    {filteredLibraries.map((library) => {
                      const on = draftScope.libraryIds.includes(library.id);
                      return (
                        <li
                          key={library.id}
                          className="flex min-w-0 w-full items-center gap-1 rounded-lg px-2 py-1 hover:bg-white/5"
                        >
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-white/10 text-slate-300">
                            <Library className="h-4 w-4" />
                          </span>
                          <span className="min-w-0 flex-1 truncate text-sm font-medium text-white">
                            {library.name}
                          </span>
                          <div className="flex shrink-0 items-center">
                            <MenuSwitch
                              checked={on}
                              disabled={disabled}
                              label={`Toggle ${library.name}`}
                              onCheckedChange={() => {
                                commitSelection(
                                  false,
                                  togglePrivateLibrary(
                                    draftScope,
                                    activeWorkspace.id,
                                    library.id,
                                  ),
                                );
                              }}
                            />
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          ) : null}

          {tab === "workspaces" && workspacePane === "public" ? (
            <div className="flex min-h-[12rem] flex-col">
              <button
                type="button"
                onClick={() => {
                  setSearchQuery("");
                  setWorkspacePane("list");
                }}
                className="mb-1 flex items-center gap-1.5 text-sm font-medium text-white"
              >
                <ArrowLeft className="h-4 w-4" />
                Public libraries
              </button>
              <div className="relative mb-2">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-500" />
                <Input
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Search libraries"
                  className="h-8 border-white/10 bg-white/5 pl-8 text-sm text-white placeholder:text-slate-500"
                  aria-label="Search public libraries"
                />
              </div>
              <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto">
                {filteredPublic.length === 0 ? (
                  <div className="py-8 text-center text-sm text-slate-400">
                    {options?.publicLibraries.length === 0
                      ? "No public libraries available."
                      : `No libraries match “${searchQuery.trim()}”`}
                  </div>
                ) : (
                  <ul>
                    {filteredPublic.map((library) => {
                      const on = draftScope.publicLibraryIds.includes(library.id);
                      return (
                        <li
                          key={library.id}
                          className="flex min-w-0 w-full items-center gap-1 rounded-lg px-2 py-1 hover:bg-white/5"
                        >
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-white/10 text-slate-300">
                            <BookOpen className="h-4 w-4" />
                          </span>
                          <span className="min-w-0 flex-1 truncate text-sm font-medium text-white">
                            {library.name}
                          </span>
                          <div className="flex shrink-0 items-center">
                            <MenuSwitch
                              checked={on}
                              disabled={disabled}
                              label={`Toggle ${library.name}`}
                              onCheckedChange={() => {
                                commitSelection(
                                  false,
                                  togglePublicLibrary(draftScope, library.id),
                                );
                              }}
                            />
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          ) : null}
      </div>
    </div>
      ) : null}
    </>
  );
}
